import React, { useEffect, useState } from 'react'
import { ScrollView, View, Platform } from 'react-native'
import {
  ATLSpacing,
  ATLTextBody,
  DialogTypeEnum,
  FontWeightEnum,
  SpacingSizesEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { atlTheme } from '../../atlTheme'
import { icons } from '../../constants/searchAssets'
import styled from 'styled-components/native'
import { IconTwoText } from './components/IconTwoText'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { getStatement } from '../../services/main/getStatement'
import { useGlobal } from '../../global/useGlobal'
import { usePromise } from '../../utils/hooks/usePromise'
import { Loading } from '../../components/loading/Loading'
import { InstallmentsCard } from './components/InstallmentsCard'
import { Accordion } from '../../components/accordion/Accordion'
import { getUserSaleContracts } from '../../services/main/getUserSaleContracts'
import { EmptyCard } from './components/EmptyCard'
import { ConstructionModuleSelector } from '../../components/ConstructionModuleSelector'
import { setActualSaleContract } from '../../services/main/changeActualSaleContract'
import { listUserSaleContracts_listUserSaleContracts } from '../../services/types/listUserSaleContracts'
import { useGetSecondBankSlip } from './utils/useGetSecondBankSlip'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { useIsFocused } from '@react-navigation/native'
import { onPressSAC } from './utils/onPressSAC'
import { Button } from '@material-ui/core'
import { useLegacyState } from '../../utils/hooks/useLegacyState'
import { Modal } from './components/Modal'
import { isWeb } from '../../utils/isWeb'

interface CurrentSaleContract extends listUserSaleContracts_listUserSaleContracts {}

const TopWhiteCard = styled.View`
  background-color: ${atlTheme.ColorSurface};
  margin-vertical: ${atlTheme.SpacingAtomic}px;
  border-bottom-left-radius: ${atlTheme.BorderRadiusCard}px;
  border-bottom-right-radius: ${atlTheme.BorderRadiusCard}px;
`

const GenericWhiteCard = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-radius: ${atlTheme.BorderRadiusCard}px;
  margin-vertical: ${atlTheme.SpacingAtomic}px;
`

const ContainerComponent: any = isWeb() ? props => <div {...props} /> : ScrollView

export const FinanceBillsScreen: React.FC = () => {
  const [installmentsOpen] = useGlobal('installmentsOpen')
  const [installmentsOverdue] = useGlobal('installmentsOverdue')
  const [installmentsPaid] = useGlobal('installmentsPaid')
  const [statement] = useGlobal('statement')
  const [userSaleContracts] = useGlobal('userSaleContracts')
  const [secondBankSlipRegistered] = useGlobal('duplicatesBankSlip')
  const [actualSaleContract] = useGlobal('actualSaleContract')
  const isFocused = useIsFocused()
  const [modalState, setModalState] = useLegacyState({
    errorMessage: undefined as string | undefined,
    onDismiss: () => null,
    onConfirm: () => null,
  })

  const [currentDropdownSaleContract, setCurrentDropdownSaleContract] = useState<CurrentSaleContract>()

  const { loading: isGetConstructionsLoading, request: getConstructions } = usePromise(getUserSaleContracts)
  const { loading: isGetInfoLoading, request: getInfo } = usePromise(getStatement)
  const clearModal = () => setModalState({ errorMessage: undefined })
  const { getDuplicateBankSlip, loadingBankSlip } = useGetSecondBankSlip(
    Coordinator.goToPayInstallment,
    installments => {
      setModalState({
        errorMessage: formatMessage(messages.financeBillsScreen_modal_error_generate_billet),
        onDismiss: clearModal,
        onConfirm: () => {
          clearModal()
          onPressSAC(installments)
        },
      })
    }
  )

  useEffect(() => {
    getConstructions()
    if (!currentDropdownSaleContract && userSaleContracts?.[0]) {
      setCurrentDropdownSaleContract(userSaleContracts[0])
      setActualSaleContract(userSaleContracts[0])
    }
  }, [])

  useEffect(() => {
    if (isFocused) {
      currentDropdownSaleContract && getInfo({ saleContractId: currentDropdownSaleContract.id })
    }
  }, [isFocused])

  useEffect(() => {
    currentDropdownSaleContract && getInfo({ saleContractId: currentDropdownSaleContract.id })
    setActualSaleContract(currentDropdownSaleContract)
  }, [currentDropdownSaleContract])

  const onChangeDropdown = item => {
    setCurrentDropdownSaleContract(userSaleContracts[item?.id])
  }

  const payedPercentage = statement?.payedPercentage?.toFixed(2) ?? 0

  return (
    <ContainerComponent>
      {isGetInfoLoading || isGetConstructionsLoading || !userSaleContracts || loadingBankSlip ? (
        <>
          <ATLSpacing type={SpacingSizesEnum.marginal} />
          {loadingBankSlip && (
            <View style={{ width: '100%' }}>
              <ATLTextBody textAlignment={TextAlignmentEnum.center} labelBaseProps={{ multilineLimit: 4 }}>
                {messages.financeFutureScreen_loadingNewFutureBillets}
              </ATLTextBody>
            </View>
          )}
          <Loading />
        </>
      ) : (
        <>
          <TopWhiteCard>
            <ConstructionModuleSelector onChange={onChangeDropdown} />
            <IconTwoText
              icon={icons.monetization}
              button={false}
              firstTextHorizontal={formatMessage(messages.financeBillsScreen_totalPaid)}
              secondTextHorizontal={`${payedPercentage}%`}
            />
          </TopWhiteCard>
          <InstallmentsCard
            icon={icons.warningIcon}
            data={installmentsOverdue}
            installmentType={formatMessage(messages.financeScreen_installmentType_expired)}
            button={true}
            buttonTitle={formatMessage(messages.financeBillsScreen_button_duplicate)}
            currentSaleContract={actualSaleContract}
            getDuplicateBankSlip={getDuplicateBankSlip}
            secondBankSlipRegistered={secondBankSlipRegistered}
            isInstallmentsOverdue
          />
          <InstallmentsCard
            icon={icons.warningOutlineIcon}
            data={installmentsOpen}
            installmentType={formatMessage(messages.financeScreen_installmentType_open)}
            button={true}
            buttonTitle={formatMessage(messages.financeBillsScreen_button_pay)}
            getDuplicateBankSlip={getDuplicateBankSlip}
          />
          {installmentsPaid?.length ? (
            <GenericWhiteCard>
              <Accordion
                title={`${formatMessage(messages.financeScreen_installments)} ${formatMessage(
                  messages.financeScreen_installmentType_paid
                )}`}
                installments={installmentsPaid}
              />
            </GenericWhiteCard>
          ) : (
            <EmptyCard type={formatMessage(messages.financeScreen_installmentType_paid)} />
          )}
        </>
      )}
      <Modal
        type={DialogTypeEnum.alert}
        open={!!modalState.errorMessage}
        onClose={modalState.onDismiss}
        onPressDismiss={modalState.onDismiss}
        onCancelButton={modalState.onDismiss}
        onPressConfirm={modalState.onConfirm}
        onConfirmButton={modalState.onConfirm}
        title={formatMessage(messages.financeBillsScreen_modal_title)}
        description={modalState.errorMessage}
        messageConfirmButton={messages.financeScreen_dialogButton_request}
        messageCancelButton={messages.financeScreen_dialogButton_cancel}
        buttonTexts={[
          formatMessage(messages.financeScreen_dialogButton_cancel),
          formatMessage(messages.financeScreen_dialogButton_request),
        ]}
        buttonBaseProps={{ textColorComplementary: atlTheme.ColorBrand2High, fontWeight: FontWeightEnum.medium }}
        titleLabelBaseProps={{ fontWeight: FontWeightEnum.medium }}
      />
    </ContainerComponent>
  )
}
